import type { UnifiedFilters } from 'types/filters';

const VALUE_PLACEHOLDER_RE = /^\$value\(([^)]+)\)$/;

export interface ResolveResult {
  resolved: UnifiedFilters;
  /** Sibling property names referenced by $value() whose value was absent or null. */
  unresolvedSiblings: string[];
}

/**
 * Returns every sibling property name referenced by $value(<name>) in the
 * propertyFilters or perspectiveFilters (assignees) of the given filter. Used
 * to subscribe a dependent field's query to the correct Formik sibling values.
 */
export function extractValuePlaceholderDeps(filter?: UnifiedFilters): string[] {
  if (!filter) return [];
  const deps: string[] = [];
  for (const pf of filter.propertyFilters ?? []) {
    const m = VALUE_PLACEHOLDER_RE.exec(String(pf.value ?? ''));
    if (m) deps.push(m[1]);
  }
  for (const persp of filter.perspectiveFilters ?? []) {
    for (const assignee of persp.assignees ?? []) {
      const m = VALUE_PLACEHOLDER_RE.exec(String(assignee ?? ''));
      if (m) deps.push(m[1]);
    }
  }
  return deps;
}

/**
 * Substitutes $value(<sibling>) placeholders in a filter's propertyFilters
 * (value) and perspectiveFilters (each assignee entry) with the actual values
 * from siblingValues.
 *
 * - Non-placeholder values are passed through unchanged.
 * - If a sibling value is absent or null, the sibling name is added to
 *   unresolvedSiblings and the original placeholder string is left in place
 *   (callers must treat any unresolvedSiblings as an error / skip-query).
 */
export function resolveValuePlaceholders(
  filter: UnifiedFilters,
  siblingValues: Record<string, any>,
): ResolveResult {
  const unresolvedSiblings: string[] = [];

  if (!filter) {
    return { resolved: filter, unresolvedSiblings };
  }

  const resolveValue = (raw: any): any => {
    const m = VALUE_PLACEHOLDER_RE.exec(String(raw ?? ''));
    if (!m) return raw;

    const siblingName = m[1];
    const siblingVal = siblingValues[siblingName];

    if (siblingVal == null) {
      unresolvedSiblings.push(siblingName);
      return raw;
    }

    return siblingVal;
  };

  const resolved: UnifiedFilters = { ...filter };

  if (filter.propertyFilters) {
    resolved.propertyFilters = filter.propertyFilters.map((pf) => ({
      ...pf,
      value: resolveValue(pf.value),
    }));
  }

  if (filter.perspectiveFilters) {
    resolved.perspectiveFilters = filter.perspectiveFilters.map((persp) => ({
      ...persp,
      assignees: (persp.assignees ?? []).map(resolveValue),
    }));
  }

  return { resolved, unresolvedSiblings };
}
